import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import projectService from '../services/projectService';
import documentService from '../services/documentService';

/**
 * Everything about a project that is not a campaign.
 *
 * This replaces the four-step wizard. Creating a project genuinely is a
 * sequence — once. Editing one is not: somebody comes here to change the
 * product description, and having to click Next past two screens they did not
 * want to touch is what made updates a hectic job.
 *
 * So: one page, three tabs, and every field saves itself where it is edited.
 * There is no Save button for the same reason there is no Next — a page that
 * saves on blur cannot lose an edit to a mistimed navigation.
 *
 * Audience is deliberately absent. It belongs to the campaign now, because two
 * campaigns for one product can want entirely different people.
 */

const Container = styled.div`
  max-width: 860px;
  margin: 0 auto;
  padding: 2rem;
`;

const Title = styled.h1`
  font-family: var(--font-heading);
  font-size: var(--text-3xl);
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  font-size: 1.05rem;
  margin: 0.75rem 0 1.75rem;
  line-height: 1.6;
  max-width: 62ch;
`;

const Tabs = styled.div`
  display: flex;
  gap: 0.35rem;
  border-bottom: 1px solid var(--border-color);
  margin-bottom: 1.5rem;
  overflow-x: auto;
`;

const Tab = styled.button`
  background: none;
  border: none;
  border-bottom: 2px solid ${(p) => (p.$active ? 'var(--accent-cyan)' : 'transparent')};
  color: ${(p) => (p.$active ? 'var(--text-primary)' : 'var(--text-secondary)')};
  font-size: 0.95rem;
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  padding: 0.7rem 1.1rem;
  cursor: pointer;
  white-space: nowrap;

  &:hover { color: var(--text-primary); }
`;

const Panel = styled.div`
  background: var(--gradient-card);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  padding: 1.5rem;
  margin-bottom: 1.25rem;
`;

const Field = styled.div`
  & + & { margin-top: 1.6rem; }
`;

const LabelRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.45rem;
`;

const Label = styled.label`
  color: var(--text-primary);
  font-size: 0.95rem;
  font-weight: 600;
`;

const Hint = styled.p`
  color: var(--text-muted);
  font-size: 0.82rem;
  line-height: 1.5;
  margin: 0 0 0.55rem;
  max-width: 62ch;
`;

/** Says what happened to this one field, next to that one field. */
const State = styled.span`
  font-size: 0.78rem;
  white-space: nowrap;
  color: ${(p) => (p.$tone === 'bad' ? '#f87171' : p.$tone === 'good' ? 'var(--accent-cyan)' : 'var(--text-muted)')};
`;

const inputStyles = `
  width: 100%;
  background: rgba(22, 26, 43, 0.8);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: 0.8rem 1rem;
  color: var(--text-primary);
  font-family: inherit;
  font-size: 0.95rem;
  line-height: 1.55;

  &:focus { outline: none; border-color: var(--accent-cyan); }
  &::placeholder { color: var(--text-muted); }
`;

const Input = styled.input`${inputStyles}`;
const TextArea = styled.textarea`
  ${inputStyles}
  min-height: ${(p) => p.$tall || '7rem'};
  resize: vertical;
`;

const Drop = styled.div`
  border: 1px dashed ${(p) => (p.$over ? 'var(--accent-cyan)' : 'var(--border-color)')};
  background: ${(p) => (p.$over ? 'rgba(34,211,238,0.06)' : 'transparent')};
  border-radius: var(--radius-md);
  padding: 1.6rem;
  text-align: center;
  cursor: pointer;
  color: var(--text-secondary);

  &:hover { border-color: var(--accent-cyan); }
`;

const DocRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.75rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.05);

  &:first-of-type { border-top: none; }
`;

const DocName = styled.div`
  flex: 1;
  min-width: 0;
  color: var(--text-primary);
  font-size: 0.92rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DocMeta = styled.div`
  color: var(--text-muted);
  font-size: 0.78rem;
  margin-top: 0.15rem;
`;

const Chip = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  white-space: nowrap;
  background: ${(p) => (p.$done ? 'rgba(34,211,238,0.12)' : 'rgba(251,191,36,0.12)')};
  color: ${(p) => (p.$done ? 'var(--accent-cyan)' : '#fbbf24')};
  border: 1px solid ${(p) => (p.$done ? 'rgba(34,211,238,0.35)' : 'rgba(251,191,36,0.3)')};
`;

const Button = styled.button`
  border-radius: var(--radius-md);
  padding: 0.5rem 0.95rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--border-color);

  &:hover { color: var(--text-primary); border-color: var(--accent-cyan); }
`;

const Message = styled.div`
  padding: 3rem 2rem;
  text-align: center;
  color: var(--text-secondary);
`;

const Notice = styled.div`
  border: 1px solid rgba(251, 191, 36, 0.4);
  background: rgba(251, 191, 36, 0.07);
  border-radius: var(--radius-md);
  padding: 0.9rem 1.1rem;
  color: var(--text-secondary);
  line-height: 1.55;
  margin-bottom: 1.25rem;
`;

const TABS = [
  { key: 'product', label: 'Product' },
  { key: 'voice', label: 'Voice' },
  { key: 'documents', label: 'Documents' }
];

const readableSize = (bytes) => {
  if (!bytes) return '';
  const units = ['B', 'KB', 'MB'];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i += 1; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

/**
 * One self-saving field.
 *
 * Saves on blur rather than on every keystroke: a request per character is
 * wasteful, and a person mid-sentence has not decided anything yet.
 */
function SavedField({ name, label, hint, value, onSave, as = 'input', ...rest }) {
  const [draft, setDraft] = useState(value || '');
  const [state, setState] = useState(null);
  const saved = useRef(value || '');

  useEffect(() => {
    setDraft(value || '');
    saved.current = value || '';
  }, [value]);

  const commit = async () => {
    if (draft === saved.current) return;
    setState({ tone: 'muted', text: 'Saving…' });
    try {
      await onSave(name, draft);
      saved.current = draft;
      setState({ tone: 'good', text: 'Saved' });
      setTimeout(() => setState((s) => (s && s.text === 'Saved' ? null : s)), 2200);
    } catch (err) {
      setState({ tone: 'bad', text: err?.response?.data?.message || 'Not saved' });
    }
  };

  const Control = as === 'textarea' ? TextArea : Input;

  return (
    <Field>
      <LabelRow>
        <Label htmlFor={name}>{label}</Label>
        {state && <State $tone={state.tone}>{state.text}</State>}
      </LabelRow>
      {hint && <Hint>{hint}</Hint>}
      <Control
        id={name}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        {...rest}
      />
    </Field>
  );
}

function Settings() {
  const projectId = projectService.current();
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState('product');
  const [error, setError] = useState('');
  const [readOnly, setReadOnly] = useState(false);

  const [documents, setDocuments] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(null);
  const fileInput = useRef(null);

  const load = useCallback(async () => {
    if (!projectId) { setError('Pick a project first.'); return; }
    try {
      setError('');
      setSettings(await projectService.settings(projectId));
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not load these settings');
    }
  }, [projectId]);

  const loadDocuments = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await documentService.getDocuments(projectId);
      setDocuments((response.data && response.data.documents) || []);
    } catch {
      // A project with no documents yet is the normal case, not a failure.
      setDocuments([]);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === 'documents') loadDocuments(); }, [tab, loadDocuments]);

  const save = async (field, value) => {
    try {
      const next = await projectService.saveSettings(projectId, { [field]: value });
      setSettings(next);
      setReadOnly(false);
    } catch (err) {
      if (err?.response?.status === 403) setReadOnly(true);
      throw err;
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading({ name: file.name, percent: 0 });
    try {
      await documentService.uploadDocument(projectId, file, (e) => {
        if (e.total) setUploading({ name: file.name, percent: Math.round((e.loaded * 100) / e.total) });
      });
      await loadDocuments();
    } catch (err) {
      setError(err?.response?.data?.message || `Could not upload ${file.name}`);
    } finally {
      setUploading(null);
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Remove "${doc.name}"? Orion will stop using it to write emails.`)) return;
    try {
      await documentService.deleteDocument(projectId, doc._id);
      await loadDocuments();
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not remove that document');
    }
  };

  if (!settings && !error) return <Container><Message>Loading…</Message></Container>;
  if (!settings) return <Container><Panel>{error}</Panel></Container>;

  return (
    <Container>
      <Title>{settings.name || 'Settings'}</Title>
      <Subtitle>
        What Orion knows about this product and how it writes about it. Every box
        saves as you leave it — there is nothing to submit.
      </Subtitle>

      {readOnly && (
        <Notice>You have read-only access to this project, so these settings are not yours to change.</Notice>
      )}
      {error && <Notice>{error}</Notice>}

      <Tabs role="tablist">
        {TABS.map((t) => (
          <Tab
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            $active={tab === t.key}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </Tab>
        ))}
      </Tabs>

      {tab === 'product' && (
        <Panel>
          <SavedField
            name="productDescription"
            label="What this product does"
            hint="Written for a buyer, not a brochure. This is the single biggest influence on how the emails read, so it is worth a few real sentences."
            value={settings.productDescription}
            onSave={save}
            as="textarea"
            $tall="9rem"
            placeholder="Directure lets a small team shoot a film the way software gets built…"
          />
          <SavedField
            name="productUrl"
            label="Website"
            hint="Used for research and cited in emails when it helps."
            value={settings.productUrl}
            onSave={save}
            placeholder="https://directure.ai"
          />
        </Panel>
      )}

      {tab === 'voice' && (
        <Panel>
          <SavedField
            name="name"
            label="Project name"
            hint="What this workspace is called, in the switcher and on every screen."
            value={settings.name}
            onSave={save}
            maxLength={100}
          />
          <SavedField
            name="description"
            label="What this project is for"
            hint="One or two lines. Orion uses it to keep a campaign on-topic."
            value={settings.description}
            onSave={save}
            as="textarea"
            maxLength={1000}
          />
          <SavedField
            name="messagingStyle"
            label="How the emails should sound"
            hint="Paste an email you would actually have sent. An example teaches the tone far better than adjectives do — leave it empty and Orion writes plainly."
            value={settings.messagingStyle}
            onSave={save}
            as="textarea"
            $tall="10rem"
            maxLength={2000}
            placeholder={'Hi Priya,\n\nSaw Nirvana wrapped a virtual-production short last month…'}
          />
        </Panel>
      )}

      {tab === 'documents' && (
        <Panel>
          <Hint style={{ marginBottom: '1rem' }}>
            Decks, one-pagers, case studies. Orion reads these when it researches
            a lead, so a claim in an email can come from your own material rather
            than from a guess.
          </Hint>

          <Drop
            $over={dragOver}
            onClick={() => fileInput.current && fileInput.current.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files[0]); }}
          >
            <input
              ref={fileInput}
              type="file"
              accept=".pdf,.doc,.docx,.txt,.md"
              style={{ display: 'none' }}
              onChange={(e) => { upload(e.target.files[0]); e.target.value = ''; }}
            />
            {uploading
              ? `Uploading ${uploading.name}… ${uploading.percent}%`
              : 'Drop a file here, or click to choose one — PDF, Word, or text'}
          </Drop>

          {documents.length > 0 && (
            <div style={{ marginTop: '1.25rem' }}>
              {documents.map((doc) => (
                <DocRow key={doc._id}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <DocName>{doc.name}</DocName>
                    <DocMeta>
                      {readableSize(doc.size)}
                      {doc.uploadedAt ? ` · ${new Date(doc.uploadedAt).toLocaleDateString()}` : ''}
                    </DocMeta>
                  </div>
                  <Chip $done={doc.ragProcessed}>
                    {doc.ragProcessed ? 'Readable' : 'Being read'}
                  </Chip>
                  <Button onClick={() => remove(doc)}>Remove</Button>
                </DocRow>
              ))}
            </div>
          )}

          {documents.length === 0 && !uploading && (
            <Hint style={{ marginTop: '1rem' }}>Nothing here yet.</Hint>
          )}
        </Panel>
      )}
    </Container>
  );
}

export default Settings;
